/**
 * スタンプラリーの画面（一覧と詳細）
 *
 * 地図と常設シートは MapShell が持つので、ここは載せる中身だけを組み立てる。
 * URL が /rallies 配下でないあいだは取得も止めて、ルートプラン側に地図を明け渡す。
 */

import { ChevronDown, MapPin, Route, Stamp, Trophy } from 'lucide-react'
import { type RefObject, useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { toast } from 'sonner'

import type { RallyMapItem, StampRallySummary } from '@/api/types/stamp-rally'
import { RallyFilterBar } from '@/components/shared/map/rally-filter-bar'
import { RallyMapLayer } from '@/components/shared/map/rally-map-layer'
import type { RallyPlaceGroup } from '@/components/shared/map/rally-place-popup'
import { useRallyPlaces } from '@/components/shared/map/use-rally-places'
import { SheetNavHeader } from '@/components/shared/sheet-nav-header'
import type { BottomSheetRef } from '@/components/ui/bottom-sheet'
import { Button } from '@/components/ui/button'
import type { ActionSection } from '@/components/ui/custom-dropdown'
import { Skeleton } from '@/components/ui/skeleton'
import type { Coords } from '@/hooks/useCurrentPosition'
import { cn } from '@/lib/utils'
import type { MapScreen, SheetSnap } from '@/pages/map/types'
import { PLAN_MAX_SPOTS } from '@/pages/route-plans/lib'
import { PATHS } from '@/routes/paths'
import { useStampRallyStore } from '@/store/useStampRallyStore'

import { RallyCard } from './components/rally-card'
import { RallyFormPanel } from './components/rally-form-panel'
import { matchesItemFilters } from './detail/lib'
import { useRallyDetailPanel } from './detail/use-rally-detail-panel'
import { isRallyComplete } from './lib'

const UNCATEGORIZED = 'その他'

const detailPath = (rallyId: number) => PATHS.RALLIES.DETAIL.replace(':rally_id', String(rallyId))

/** 近接通知から渡される場所 ID。近い順に並んでいる */
const PLACES_PARAM = 'places'

const parsePlaceIds = (value: string): number[] =>
  value
    .split(',')
    .map(Number)
    .filter((id) => Number.isInteger(id) && id > 0)

type Section = {
  category: string
  rallies: StampRallySummary[]
  total: number
  stamped: number
}

const buildSections = (rallies: StampRallySummary[]): Section[] => {
  const byCategory = new Map<string, StampRallySummary[]>()
  for (const rally of rallies) {
    const key = rally.category || UNCATEGORIZED
    byCategory.set(key, [...(byCategory.get(key) ?? []), rally])
  }
  return [...byCategory.entries()].map(([category, items]) => ({
    category,
    rallies: items,
    total: items.reduce((sum, r) => sum + r.total_count, 0),
    stamped: items.reduce((sum, r) => sum + r.stamped_count, 0),
  }))
}

type Args = {
  /** false なら別画面が地図を使っている。取得も副作用も止める */
  active: boolean
  /** null なら一覧を見ている */
  rallyId: number | null
  origin: Coords | null
  requestPosition: () => Promise<Coords | null>
  sheetRef: RefObject<BottomSheetRef | null>
  snap: SheetSnap
}

export const useRallyScreen = ({ active, rallyId, origin, requestPosition, sheetRef, snap }: Args): MapScreen => {
  const navigate = useNavigate()

  const [isCreating, setIsCreating] = useState(false)
  const [activePlaceId, setActivePlaceId] = useState<number | null>(null)
  const [selected, setSelected] = useState<RallyPlaceGroup[]>([])
  // 絞り込みは地図にも掛けるので、詳細パネルではなくこちらが持つ
  const [keyword, setKeyword] = useState('')
  const [prefectures, setPrefectures] = useState<string[]>([])
  const [showCompleted, setShowCompleted] = useState(false)

  const rallies = useStampRallyStore((s) => s.rallies)
  const isListLoading = useStampRallyStore((s) => s.isListLoading)
  const error = useStampRallyStore((s) => s.error)
  const fetchRallies = useStampRallyStore((s) => s.fetchRallies)
  const createRally = useStampRallyStore((s) => s.createRally)
  const clearError = useStampRallyStore((s) => s.clearError)

  const [searchParams, setSearchParams] = useSearchParams()
  const placesParam = active ? (searchParams.get(PLACES_PARAM) ?? '') : ''
  const focusPlaceIds = useMemo(() => parsePlaceIds(placesParam), [placesParam])

  const activeRallies = useMemo(() => rallies.filter((rally) => !isRallyComplete(rally)), [rallies])
  const completedRallies = useMemo(() => rallies.filter(isRallyComplete), [rallies])
  // 一覧に出ているラリーだけ地図にも出す。コンプリート済みは畳みを開いたときだけ
  const listedRallies = showCompleted ? rallies : activeRallies
  const hiddenCompletedIds = useMemo(
    () => (showCompleted ? [] : completedRallies.map((rally) => rally.id)),
    [showCompleted, completedRallies],
  )

  const filterItem = useCallback(
    (item: RallyMapItem) =>
      // 詳細で開いているラリーはコンプリート済みでも出す
      (item.rally_id === rallyId || !hiddenCompletedIds.includes(item.rally_id)) &&
      (focusPlaceIds.length === 0 || focusPlaceIds.includes(item.place_id)) &&
      matchesItemFilters(item, keyword, prefectures),
    [rallyId, hiddenCompletedIds, focusPlaceIds, keyword, prefectures],
  )

  const rallyPlaces = useRallyPlaces({
    rallies: listedRallies,
    enabled: active,
    keepPlaceId: activePlaceId,
    focusRallyId: rallyId,
    filterItem,
    filterKey: `${placesParam}-${showCompleted}`,
  })

  const detailPanel = useRallyDetailPanel({
    rallyId: active ? rallyId : null,
    rallyPlaces,
    origin,
    requestPosition,
    keyword,
    onKeywordChange: setKeyword,
    selectedPrefectures: prefectures,
    onActivatePlace: setActivePlaceId,
    sheetRef,
    snap,
  })

  useEffect(() => {
    if (!active) return
    void fetchRallies()
  }, [active, fetchRallies])

  useEffect(() => {
    if (!active || !error) return
    toast.error(error)
    clearError()
  }, [active, error, clearError])

  // ラリーを移ったら前のラリー向けの絞り込みは意味がないので落とす
  useEffect(() => {
    setKeyword('')
    setPrefectures([])
    setActivePlaceId(null)
    setIsCreating(false)
  }, [rallyId])

  // 通知から来たときは、いちばん近い場所のポップアップを開いた状態で見せる
  useEffect(() => {
    if (focusPlaceIds.length === 0) return
    setActivePlaceId(focusPlaceIds[0])
  }, [focusPlaceIds])

  const openRally = (id: number) => {
    navigate(detailPath(id))
    sheetRef.current?.snapTo(snap.half)
  }

  const toggleSelected = (place: RallyPlaceGroup) =>
    setSelected((prev) =>
      prev.some((p) => p.placeId === place.placeId)
        ? prev.filter((p) => p.placeId !== place.placeId)
        : [...prev, place],
    )

  const handleCreateRoute = () => {
    // ルートは場所単位で組むので、1 場所につき 1 件だけ渡す（未獲得のラリーを優先）
    const itemIds = selected.map((place) => (place.items.find((item) => !item.stamped_at) ?? place.items[0]).id)
    navigate(PATHS.ROUTE_PLANS.CREATE, { state: { itemIds } })
  }

  const selectedPlaceIds = selected.map((place) => place.placeId)
  const unselectedVisible = rallyPlaces.visible
    .map(({ place }) => place)
    .filter((place) => !selectedPlaceIds.includes(place.placeId))

  // 絞り込みに合う場所をまとめて選ぶ。ルートに渡せる件数を超える分は足さない
  const selectAllVisible = () => {
    const room = Math.max(PLAN_MAX_SPOTS - selected.length, 0)
    if (unselectedVisible.length > room) toast.warning(`ルートに入れられるのは ${PLAN_MAX_SPOTS} 件までです`)
    setSelected([...selected, ...unselectedVisible.slice(0, room)])
  }

  const isDetail = rallyId !== null

  const menuSections: ActionSection[] = [
    {
      type: 'action',
      label: 'ルート',
      items: [
        { key: 'route-plan-create', label: 'ルートを作る', onClick: () => navigate(PATHS.ROUTE_PLANS.CREATE) },
        { key: 'route-plans', label: 'ルート一覧', onClick: () => navigate(PATHS.ROUTE_PLANS.LIST) },
      ],
    },
  ]

  const listHeader = isCreating ? (
    <SheetNavHeader onBack={() => setIsCreating(false)} title="ラリーを作成" />
  ) : (
    <SheetNavHeader
      title={<span className="tabular-nums text-muted-foreground">{activeRallies.length} 件のラリー</span>}
      action={
        <Button
          size="sm"
          variant="outline"
          className="px-3!"
          onClick={() => {
            setIsCreating(true)
            sheetRef.current?.snapTo(snap.full)
          }}
        >
          追加
        </Button>
      }
    />
  )

  const listBody = isCreating ? (
    <RallyFormPanel
      onSubmit={async (params) => {
        const created = await createRally(params)
        if (!created) return
        setIsCreating(false)
        navigate(detailPath(created.id))
      }}
    />
  ) : (
    <div className="p-3">
      {isListLoading && rallies.length === 0 ? (
        <div className="space-y-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-xl" />
          ))}
        </div>
      ) : rallies.length === 0 ? (
        <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
          <Stamp className="size-10" />
          <p className="text-sm">まだスタンプラリーがありません</p>
          <p className="text-xs">「追加」から作成できます</p>
        </div>
      ) : (
        <div className="space-y-6">
          {activeRallies.length === 0 && (
            <p className="py-10 text-center text-sm text-muted-foreground">進行中のラリーはありません</p>
          )}
          {buildSections(activeRallies).map((section) => (
            <section key={section.category} className="space-y-3">
              <div className="flex items-baseline justify-between px-1">
                <h2 className="text-sm font-medium text-muted-foreground">{section.category}</h2>
                <span className="text-xs tabular-nums text-muted-foreground">
                  {section.stamped} / {section.total}
                </span>
              </div>
              {section.rallies.map((rally) => (
                <RallyCard key={rally.id} rally={rally} onClick={() => openRally(rally.id)} />
              ))}
            </section>
          ))}

          {completedRallies.length > 0 && (
            <section className="space-y-3">
              <button
                type="button"
                className="flex w-full items-center gap-2 rounded-md px-1 py-2 text-xs text-muted-foreground hover:bg-muted/50"
                onClick={() => setShowCompleted((current) => !current)}
              >
                <Trophy size={13} />
                コンプリートしたラリー
                <span className="tabular-nums">{completedRallies.length}</span>
                <ChevronDown size={14} className={cn('ml-auto transition-transform', showCompleted && 'rotate-180')} />
              </button>
              {showCompleted &&
                completedRallies.map((rally) => (
                  <RallyCard key={rally.id} rally={rally} onClick={() => openRally(rally.id)} />
                ))}
            </section>
          )}
        </div>
      )}
    </div>
  )

  return {
    title: isDetail ? (detailPanel.title ?? 'スタンプラリー') : 'スタンプラリー',
    menuSections,
    banner: focusPlaceIds.length > 0 && (
      <button
        onClick={() => {
          searchParams.delete(PLACES_PARAM)
          setSearchParams(searchParams, { replace: true })
        }}
        className="flex w-full shrink-0 items-center justify-center gap-1.5 border-b bg-primary/10 px-3 py-2 text-xs text-primary"
      >
        <MapPin className="size-3.5" />
        近くの未スタンプ {focusPlaceIds.length} 件を表示中 · タップで解除
      </button>
    ),
    filterBar: (
      <RallyFilterBar
        count={rallyPlaces.visible.length}
        selectedCount={selected.length}
        onSelectAll={unselectedVisible.length > 0 ? selectAllVisible : undefined}
        stampFilter={rallyPlaces.stampFilter}
        onStampFilterChange={rallyPlaces.setStampFilter}
        legend={isDetail ? [] : rallyPlaces.legend}
        hiddenRallyIds={rallyPlaces.hiddenRallyIds}
        onToggleRally={rallyPlaces.toggleRally}
        onToggleAllRallies={rallyPlaces.setAllRalliesHidden}
        planLegend={rallyPlaces.planLegend}
        hiddenPlanIds={rallyPlaces.hiddenPlanIds}
        onTogglePlan={rallyPlaces.togglePlan}
        onToggleAllPlans={rallyPlaces.setAllPlansHidden}
        prefectures={isDetail ? detailPanel.prefectures : undefined}
        selectedPrefectures={prefectures}
        onTogglePrefecture={
          isDetail
            ? (value) =>
                setPrefectures((prev) =>
                  prev.includes(value) ? prev.filter((name) => name !== value) : [...prev, value],
                )
            : undefined
        }
        onClearPrefectures={() => setPrefectures([])}
        sortOptions={isDetail ? detailPanel.sortOptions : undefined}
        sortValue={detailPanel.sort}
        onSortChange={isDetail ? detailPanel.onSortChange : undefined}
      />
    ),
    mapLayer: (
      <RallyMapLayer
        rallyPlaces={rallyPlaces}
        activePlaceId={activePlaceId}
        onActivate={setActivePlaceId}
        selectedPlaceIds={selectedPlaceIds}
        onToggle={toggleSelected}
        onSelectItem={(item) => (item.rally_id === rallyId ? detailPanel.selectItem(item) : openRally(item.rally_id))}
        onStampChanged={detailPanel.refresh}
      />
    ),
    mapOverlay: selected.length > 0 && (
      <div className="absolute inset-x-3 top-3 flex gap-2">
        <Button variant="secondary" onClick={() => setSelected([])}>
          解除
        </Button>
        <Button className="flex-1" onClick={handleCreateRoute}>
          <Route className="mr-2 size-4" />
          {selected.length} 件でルートを作る
        </Button>
      </div>
    ),
    onMapClick: () => setActivePlaceId(null),
    fitPoints: rallyPlaces.points,
    fitKey: rallyPlaces.fitKey,
    sheetHeader: isDetail ? detailPanel.header : listHeader,
    sheetBody: isDetail ? detailPanel.body : listBody,
  }
}
